import { GridColDef, GridValidRowModel } from '@mui/x-data-grid-pro'
import {
  ButtonTypes,
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import Button from 'ext-ui/components/Button/Button'
import React, { useState } from 'react'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import AddDeptMembersForm from 'src/components/AddDeptMembersForm/AddDeptMembersForm'
import Box from 'src/components/Box/Box'
import DataGridCell from 'src/components/DataGrid/cells/DataGridCell/DataGridCell'
import DataGridCellWrapper from 'src/components/DataGrid/cells/DataGridCellWrapper/DataGridCellWrapper'
import PillCell from 'src/components/DataGrid/cells/PillCell/PillCell'
import UserAvatarCell from 'src/components/DataGrid/cells/UserAvatarCell/UserAvatarCell'
import DataGrid, {
  DataGridHeaderStyle,
  DataGridRowHeight,
} from 'src/components/DataGrid/DataGrid'
import MoreActionsCell from 'src/components/DataGrid/tables/DepartmentsDataGrid/cells/MoreActionsCell'
import DynamicSuspenseContainer from 'src/components/DynamicSuspenseContainer/DynamicSuspenseContainer'
import LoadingShimmer from 'src/components/LoadingShimmer/LoadingShimmer'
import ResourceCenterLink from 'src/components/ResourceCenterLink/ResourceCenterLink'
import SvgExpand from 'src/components/Svgs/SvgExpand'
import SvgMore from 'src/components/Svgs/SvgMore'
import SvgTrash from 'src/components/Svgs/SvgTrash'
import { ImageNames } from 'src/enums'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import { useTranslate } from 'src/localization/Trns'
import { UserListModel } from 'src/models/user.models'
import {
  useDeleteDepartmentMember,
  useSuspenseQueryDepartmentMembers,
} from 'src/queries/organizations/department.queries'
import styled, { css } from 'styled-components'

import styles from './DepartmentMembersTable.style'

const TableTitle = styled.span`
  ${getFontCss(
    20,
    themeConstants.colors.font_primary,
    styleConstants.fontFamilies.medium,
  )}
`

interface DepartmentMembersTableEmptyStateProps {
  addMembers: () => void
}
const DepartmentMembersTableEmptyState: React.FC<
  DepartmentMembersTableEmptyStateProps
> = (props): React.ReactElement => {
  const listImage = useColorOrGrayscaleImage(ImageNames.List)
  const translate = useTranslate()
  return (
    <div
      css={css`
        margin: 40px auto;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 20px;
        text-align: center;
        width: 500px;
      `}
    >
      <img width={85} src={listImage} alt={''} />
      <span
        css={css`
          ${getFontCss(
            20,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )}
        `}
      >
        {translate('Add members to this team')}
      </span>
      <span
        css={css`
          ${getFontCss(
            16,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.regular,
          )}
        `}
      >
        {translate(
          'There are no members in this team. To add a member, use the shortcut below.',
        )}
      </span>
      <span
        css={css`
          ${getFontCss(
            16,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.regular,
          )}
        `}
      >
        {translate('Learn more about Teams in the ${resourceCenter}.', {
          resourceCenter: (
            <ResourceCenterLink
              size={HoverableLinkSizes.Large}
              underline
              regular
            />
          ),
        })}
      </span>
      <Button
        type={ButtonTypes.button}
        variant={'primary'}
        onClick={(): void => {
          props.addMembers()
        }}
      >
        {translate('+ Add Members')}
      </Button>
    </div>
  )
}

export interface DepartmentMembersTableProps {
  departmentId: string | undefined
  departmentName: string | undefined
  organizationId: string | undefined
}

interface DepartmentMembersTableRow extends GridValidRowModel {
  user: UserListModel
}

const DepartmentMembersTable: React.FC<DepartmentMembersTableProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()
  const [editing, setEditing] = useState(false)

  const { data: members } = useSuspenseQueryDepartmentMembers({
    organizationId: props.organizationId || '',
    departmentId: props.departmentId || '',
  })

  const { analytics } = useAnalyticsContext()

  const rows: DepartmentMembersTableRow[] =
    members !== undefined
      ? members.map((user, i) => {
          return {
            id: user?.id,
            user: user,
            index: i,
          }
        })
      : []

  const columns: GridColDef<DepartmentMembersTableRow>[] = [
    {
      field: 'name',
      headerName: translate('Members'),
      disableColumnMenu: true,
      sortable: false,
      hideable: false,
      flex: 1,
      minWidth: 100,
      renderCell: (params): React.ReactElement => {
        const user = params.row.user
        return <UserAvatarCell user={user} params={params} />
      },
    },
    {
      field: 'role',
      headerName: translate('Role'),
      disableColumnMenu: true,
      sortable: false,
      hideable: false,
      flex: 1,
      minWidth: 100,
      renderCell: (params): React.ReactElement => {
        const role = params.row.user.departmentMemberDisplayTagTitle
        return <PillCell params={params}>{role}</PillCell>
      },
    },
    {
      field: 'contact',
      headerName: translate('Contact'),
      disableColumnMenu: true,
      sortable: false,
      hideable: false,
      flex: 1,
      minWidth: 100,
      renderCell: (params): React.ReactElement => {
        const contact = params.row.user.email
        return <DataGridCell params={params} text={contact} />
      },
    },
    {
      field: 'moreOptions',
      disableColumnMenu: true,
      resizable: false,
      sortable: false,
      hideable: false,
      maxWidth: 72,
      flex: 1,
      renderCell: (params): React.ReactElement => {
        // need this in the row because we have to pass in the user id to the deleteDepartmentMember function and need it as a param instead of body bc its needed also in the onSuccess of the function
        // the response of this endpoint doesnt return user id so we need to pass it in at the top level
        const { mutate: deleteDepartmentMember } = useDeleteDepartmentMember(
          props.organizationId || '',
          props.departmentId || '',
          params.row.id,
        )

        return (
          <DataGridCellWrapper params={params}>
            <MoreActionsCell
              mainIcon={
                <SvgMore
                  css={css`
                    transform: scale(0.75);
                  `}
                />
              }
              actionIcon={
                <SvgTrash
                  css={css`
                    transform: scale(0.75);
                  `}
                />
              }
              actionTitle={translate('Remove from team')}
              actionSubtext={translate('Remove user from this team')}
              actionOnClick={(): void => {
                deleteDepartmentMember({
                  organizationId: props.organizationId || '',
                  departmentId: props.departmentId || '',
                  userId: params.row.id,
                })
                analytics.track('remove department member')
              }}
            />
          </DataGridCellWrapper>
        )
      },
      renderHeader: (): React.ReactElement => <></>,
    },
  ]

  const loadingFallBack = (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 16px;
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          align-self: stretch;
        `}
      >
        <TableTitle>{translate('Members')}</TableTitle>
      </div>
      <LoadingShimmer width={'100%'} />
      <LoadingShimmer width={'100%'} />
      <LoadingShimmer width={'100%'} />
      <LoadingShimmer width={'100%'} />
    </div>
  )

  return (
    <div className={'DepartmentMembersTable'} css={styles}>
      <DynamicSuspenseContainer fallback={loadingFallBack}>
        {members.length > 0 || editing ? (
          <>
            <Box
              noPadding
              noShadow
              outlineBorder
              css={css`
                overflow: hidden;
              `}
            >
              <div className={'table-header'}>
                <div className={'toolbar-container'}>
                  <div className={'toolbar-filler'} />
                  <Button
                    variant={'toolbar'}
                    dropShadow={false}
                    iconLeft={
                      <SvgExpand width={16} height={16} viewBox={'0 0 24 24'} />
                    }
                    onClick={() => setEditing(true)}
                  >
                    {translate('Add Members')}
                  </Button>
                </div>
                {editing && (
                  <AddDeptMembersForm
                    organizationId={props.organizationId || ''}
                    departmentId={props.departmentId || ''}
                    departmentName={props.departmentName || ''}
                    setEditing={setEditing}
                  />
                )}
              </div>
              <DataGrid
                height={500}
                headerStyle={DataGridHeaderStyle.NoBackground}
                rowHeight={DataGridRowHeight.Default}
                rows={rows}
                columns={columns}
                filtersBody={{}}
                disableColumnFilter
                rowSelection={false}
              />
            </Box>
          </>
        ) : (
          <DepartmentMembersTableEmptyState
            addMembers={(): void => setEditing(true)}
          />
        )}
      </DynamicSuspenseContainer>
    </div>
  )
}

export default DepartmentMembersTable
